import { useEffect, useState } from 'react';
import { Box, IconButton, Paper, Slider, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useAppState } from '../../context/LoadTestContext';
import LoadGraph from '../graphs/LoadGraph';
import StrainGaugeGraph from '../graphs/StrainGaugeGraph';
import LvdtGraph from '../graphs/LvdtGraph';
import AllSensorsGraph from './AllSensorsGraph';
import WarningsPanel, { WARNING_COLORS, WarningsTabLabel, warningLevel, warningsTabName } from './WarningsPanel';

// The new dashboard's main card: the classic view's graphs, same names and
// order, as tabs instead of a dropdown, plus All Sensors vs Time. No
// description line under the tabs (Matt's notes); each graph carries its own
// hints where they're needed.
const GRAPHS = [
  { name: 'Load vs Deflection', smooths: true },
  { name: 'Strain vs Depth', smooths: false }, // its profiles are already step averages
  { name: 'LVDT Displacement', smooths: true },
  { name: 'All Sensors vs Time', smooths: true },
];

// How much the line graphs are smoothed (display only; see smoothing.js). The
// label turns orange while it's on, so a smoothed graph isn't taken for raw.
function SmoothingControl() {
  const { smoothing, setSmoothing } = useAppState();
  const on = smoothing > 0;
  return (
    <Tooltip title='Smooths the lines on the graph only. Step loads, settlements, readings and exports use the raw readings.'>
      <Stack direction='row' alignItems='center' spacing={1.25} sx={{ minWidth: 200 }}>
        <Typography variant='caption' sx={{ whiteSpace: 'nowrap', fontWeight: on ? 600 : 400, color: on ? 'warning.main' : 'text.secondary' }}>
          Smoothing {on ? `${smoothing}%` : 'off'}
        </Typography>
        <Slider
          size='small'
          value={smoothing}
          min={0}
          max={100}
          step={5}
          onChange={(_, v) => setSmoothing(v)}
          aria-label='Smoothing'
          valueLabelDisplay='auto'
          valueLabelFormat={(v) => (v ? `${v}%` : 'Off')}
          sx={{ width: 110 }}
        />
      </Stack>
    </Tooltip>
  );
}

// selectedStep: the step picked in the strip (its number in step_summary).
// warnings (optional, the live dashboard's): { critical, warnings, loading,
// unread, onOpen } adds a Warnings tab after the graphs; onOpen is called
// while it is open. actions (the Save Dashboard button) follow the last tab.
function GraphCard({ marker, selectedStep, actions, warnings = null }) {
  const [tab, setTab] = useState(0);
  const tabs = warnings ? [...GRAPHS, { name: 'Warnings', smooths: false }] : GRAPHS;
  const current = Math.min(tab, tabs.length - 1);
  const onWarnings = Boolean(warnings) && current === GRAPHS.length;
  const onOpenWarnings = warnings?.onOpen;
  useEffect(() => { if (onWarnings) onOpenWarnings?.(); }, [onWarnings, onOpenWarnings]);
  const warningColor = warnings ? WARNING_COLORS[warningLevel(warnings.critical, warnings.warnings)] : undefined;

  // Full screen: the card covers the window (and the browser goes full screen
  // where it can). It stays in the page rather than taking the card itself
  // full screen, so menus and tooltips, which open on the page body, still show
  // over it. Esc, or the browser leaving full screen, closes it.
  const [full, setFull] = useState(false);
  const openFull = () => {
    setFull(true);
    document.documentElement.requestFullscreen?.().catch(() => { /* the window is enough */ });
  };
  useEffect(() => {
    if (!full) return undefined;
    // An open menu takes its own Esc and stops it here.
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    const onFullscreenChange = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    // The page underneath doesn't scroll meanwhile, or show the scrollbar
    // index.html keeps on <html>.
    const page = document.documentElement;
    const overflow = page.style.overflow;
    page.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      page.style.overflow = overflow;
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [full]);

  return (
    <Paper
      sx={{
        flex: '999 1 600px', minWidth: 0, display: 'flex', flexDirection: 'column', p: { xs: 1, md: 1.5 }, gap: 0.5, borderRadius: 1,
        // Below menus and tooltips (modal, 1300) so they still open over it.
        ...(full && { position: 'fixed', inset: 0, zIndex: (theme) => theme.zIndex.modal - 1, borderRadius: 0 }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1 }}>
        <Tabs
          value={current}
          onChange={(e, v) => setTab(v)}
          variant='scrollable'
          scrollButtons='auto'
          aria-label='Graph'
          // Each tab as wide as its label (not MUI's 160px minimum), so Save
          // Dashboard fits beside them on a laptop screen.
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, minWidth: 0, px: 1.5, textTransform: 'none', fontSize: 14 } }}
          // The Warnings tab is underlined in its level's colour.
          TabIndicatorProps={onWarnings ? { sx: { backgroundColor: warningColor } } : undefined}
        >
          {GRAPHS.map((g) => <Tab key={g.name} label={g.name} />)}
          {warnings && (
            <Tab
              label={<WarningsTabLabel {...warnings} />}
              aria-label={warningsTabName(warnings.critical, warnings.warnings)}
            />
          )}
        </Tabs>
        {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto', flexWrap: 'wrap' }}>
          {tabs[current].smooths && <SmoothingControl />}
          <Tooltip title={full ? 'Exit full screen' : 'Full screen'}>
            <IconButton size='small' onClick={full ? () => setFull(false) : openFull} aria-label={full ? 'Exit full screen' : 'Full screen'}>
              {full ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ position: 'relative', minHeight: 0, ...(full ? { flex: 1 } : { height: { xs: 320, md: 'clamp(380px, 52vh, 620px)' } }) }}>
        {current === 0 && <LoadGraph marker={marker} />}
        {current === 1 && <StrainGaugeGraph />}
        {current === 2 && <LvdtGraph selectedStep={selectedStep} />}
        {current === 3 && <AllSensorsGraph />}
        {onWarnings && <WarningsPanel critical={warnings.critical} warnings={warnings.warnings} />}
      </Box>
    </Paper>
  );
}

export default GraphCard;
